import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const STORAGE_PREFIX_V81 = 'alfredapi.view-state.v8.1';

const readUserScopeV81 = () => {
  try {
    const user = JSON.parse(window.localStorage.getItem('user') || '{}');
    return String(user.id || user.username || 'anonymous');
  } catch (_) {
    return 'anonymous';
  }
};

const readStoredStateV81 = (storageKey, defaults) => {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(storageKey));
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) {
      return { ...defaults };
    }
    return { ...defaults, ...stored };
  } catch (_) {
    return { ...defaults };
  }
};

export const useSessionViewStateV81 = (scope, defaults) => {
  const storageKey = useMemo(
    () => `${STORAGE_PREFIX_V81}.${readUserScopeV81()}.${scope}`,
    [scope]
  );
  const [state, setStateValue] = useState(() =>
    readStoredStateV81(storageKey, defaults)
  );
  const previousStorageKeyRef = useRef(storageKey);

  useEffect(() => {
    if (previousStorageKeyRef.current === storageKey) return;
    previousStorageKeyRef.current = storageKey;
    setStateValue(readStoredStateV81(storageKey, defaults));
  }, [defaults, storageKey]);

  const setState = useCallback(
    (nextState) => {
      setStateValue((currentState) => {
        const resolvedState =
          typeof nextState === 'function' ? nextState(currentState) : nextState;
        const safeState = { ...currentState, ...resolvedState };
        try {
          window.sessionStorage.setItem(storageKey, JSON.stringify(safeState));
        } catch (_) {}
        return safeState;
      });
    },
    [storageKey]
  );

  return [state, setState];
};

export const useSessionViewFieldV81 = (state, setState, field) => {
  const setValue = useCallback(
    (nextValue) => {
      setState((currentState) => ({
        [field]:
          typeof nextValue === 'function'
            ? nextValue(currentState[field])
            : nextValue,
      }));
    },
    [field, setState]
  );

  return [state[field], setValue];
};
